import {Row, Col, Card, Button, Input, Form, Badge, Tabs, Spin, Radio, Switch, Divider, Select, InputNumber, Alert} from "antd";
import { useState, useEffect } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import { useLocation, useNavigate, useParams } from "react-router";
import apiPath from "../../constants/apiPath";
import { Link } from "react-router-dom";
import languages from "../../config/language";
import DescriptionEditor from '../../components/DescriptionEditor';
import SegmentSelect from "../../components/select/SegmentSelect";
import { ArrowLeftIcon, CheckLineIcon } from "lucide-react";
const { TabPane } = Tabs;

const FEATURE_CONFIG = [
    {
        category: "Product Search",
        features: [
            { key: "product_search", label: "Simple & Advance Search" },
        ]
    },
    {
        category: "Price List",
        features: [
            { key: "pricelist_email_download", label: "Email & Download" },
            { key: "pricelist_inapp_view", label: "In-app View" },
            { key: "price_change_notification", label: "Price Change Notification" },
        ]
    },
    {
        category: "New Product Launch",
        features: [
            { key: "new_product_launch", label: "New Product Launch" },
        ]
    },
    {
        category: "Catalogue",
        features: [
            { key: "catalogue_email_download", label: "Email & Download" },
            { key: "catalogue_inapp_view", label: "In-app View" },
            { key: "catalogue_release_notification", label: "New Catalogue Release Notification" },
        ]
    },
    {
        category: "Schemes",
        features: [
            { key: "schemes_inapp_view", label: "In-app View" },
            { key: "schemes_notification", label: "Notification" },
        ]
    },
    {
        category: "Brands",
        features: [
            { key: "brands_list", label: "List of Brands" },
            { key: "brands_details", label: "Details" },
            { key: "brand_notification", label: "New Brand Notification" },
        ]
    },
    {
        category: "Order Placements",
        features: [
            { key: "order_placements", label: "Order Placements" },
        ]
    },
    {
        category: "Coupon Redemption",
        features: [
            { key: "coupon_redemption", label: "Coupon Redemption" },
        ]
    },
    {
        category: "Warranty For Batteries",
        features: [
            { key: "warranty_batteries", label: "Warranty For Batteries" },
        ]
    },
];

// Restriction types shown when a feature is toggled ON (OFF = no access)
const RESTRICTION_OPTIONS = [
    { value: "full", label: "Full access" },
    { value: "count", label: "Number of times" },
    { value: "once", label: "Only once" },
    { value: "days", label: "Valid days" },
];

// Paid feature: just an ON/OFF switch. ON = full access, OFF = no access.
// No restriction dropdown — usage limits apply to the trial only.
function PaidFeatureItem({ form, feature }) {
    const enabled = Form.useWatch(["features", feature.key, "enabled"], form);
    const on = enabled !== false; // undefined (untouched) defaults to ON

    return (
        <Col xs={24} sm={12} md={8} style={{ marginBottom: 16 }}>
            <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: 12, height: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ fontWeight: 500 }}>{feature.label}</span>
                    <Form.Item name={["features", feature.key, "enabled"]} valuePropName="checked" initialValue={true} noStyle>
                        <Switch checkedChildren="Y" unCheckedChildren="N" />
                    </Form.Item>
                </div>
                <div style={{ marginTop: 6, fontSize: 12, color: on ? "#52c41a" : "#ff4d4f" }}>
                    {on ? "Full access" : "No access"}
                </div>
            </div>
        </Col>
    );
}

// One feature: a Switch (off = no access) + a restriction dropdown (when on) +
// a number input for "Number of times" / "Valid days".
function FeatureItem({ form, feature }) {
    const enabled = Form.useWatch(["features", feature.key, "enabled"], form);
    const access = Form.useWatch(["features", feature.key, "access"], form);

    // Default to "full" ONLY the first time a feature is enabled, and read the
    // LIVE form value (not the lagging Form.useWatch value) so a chosen
    // restriction ("count"/"days"/"once") is never overwritten back to "full".
    useEffect(() => {
        if (enabled && !form.getFieldValue(["features", feature.key, "access"])) {
            form.setFieldValue(["features", feature.key, "access"], "full");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    return (
        <Col xs={24} sm={12} md={8} style={{ marginBottom: 16 }}>
            <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: 12, height: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ fontWeight: 500 }}>{feature.label}</span>
                    <Form.Item name={["features", feature.key, "enabled"]} valuePropName="checked" noStyle>
                        <Switch checkedChildren="Y" unCheckedChildren="N" />
                    </Form.Item>
                </div>

                {enabled && (
                    <div style={{ marginTop: 10 }}>
                        <Form.Item
                            name={["features", feature.key, "access"]}
                            style={{ marginBottom: 8 }}
                        >
                            <Select options={RESTRICTION_OPTIONS} placeholder="Restriction type" />
                        </Form.Item>

                        {access === "count" && (
                            <Form.Item
                                name={["features", feature.key, "count"]}
                                style={{ marginBottom: 0 }}
                                preserve={false}
                                rules={[{ required: true, message: "Enter number of times" }]}
                            >
                                <InputNumber min={1} style={{ width: "100%" }} placeholder="Number of times" />
                            </Form.Item>
                        )}

                        {access === "days" && (
                            <Form.Item
                                name={["features", feature.key, "days"]}
                                style={{ marginBottom: 0 }}
                                preserve={false}
                                rules={[{ required: true, message: "Enter number of days" }]}
                            >
                                <InputNumber min={1} style={{ width: "100%" }} placeholder="Valid for (days)" />
                            </Form.Item>
                        )}
                    </div>
                )}
            </div>
        </Col>
    );
}

function AddEdit() {

    const sectionName   =   "Subscription";
    const routeName     =   "subscription";
    const { id }        =   useParams();
    const isEdit        =   Boolean(id);

    const api = {
        addEdit   :  apiPath.addEditSubscription,
        edit      :  apiPath.editSubscription,
    }

    const location                          =   useLocation();
    const [form]                            =   Form.useForm();
    const { request }                       =   useRequest()
    const [loading, setLoading]             =   useState(false)
    const [fetching, setFetching]           =   useState(false);
    const navigate                          =   useNavigate();
    const [validationStatus, setValidationStatus] = useState({});
    const [type, setType] = useState(form.getFieldValue("type"));
    const [trialDays, setTrialDays] = useState(null);


    const handleFieldChange = (data, lang, type) => {
        const name                  =   form.getFieldValue(`${lang}_name`);
        const description           =   form.getFieldValue(`${lang}_description`);
        const isDescriptionEmpty    =   !description || (description.trim() === '<p></p>' || description.trim() === "");
        setValidationStatus(prevStatus => ({
            ...prevStatus,
            [lang]: !!name && !isDescriptionEmpty
        }));
    }

    const fetchData = (id) => {
        setFetching(true);
        request({
            url: api.edit + "/" + id,
            method: 'GET',
            onSuccess: (response) => {
                const { data } = response;
                form.setFieldsValue({ ...data });
                setType(data.type);
                setFetching(false);
            },
            onError: (error) => {
                setFetching(false);
                ShowToast(error, Severty.ERROR)
            }
        })
    }

    const fetchSettings = () => {
        request({
            url: apiPath.commonSetting,
            method: 'GET',
            onSuccess: (response) => {
                if (response.data) {
                    setTrialDays(response.data.free_trial);
                }
            },
        });
    };

    useEffect(() => {
        fetchSettings();
        const initialValidationStatus = {};

        languages.forEach(lang => {
            initialValidationStatus[lang.short] = !!form.getFieldValue(`${lang.short}_name`) && !!form.getFieldValue(`${lang.short}_description`);
        });

        setValidationStatus(initialValidationStatus);
    }, [])

    const onCreate = (values) => {

        let payload = {
            ...values
        };

        // Paid subscriptions have no usage metering: a feature is either ON
        // (full access) or OFF (no access). Force access = "full" for every
        // enabled feature; an untouched feature defaults to ON.
        if (payload.type === "paid") {
            const paidFeatures = {};
            FEATURE_CONFIG.forEach((group) =>
                group.features.forEach((feature) => {
                    const on = values.features?.[feature.key]?.enabled !== false;
                    paidFeatures[feature.key] = { enabled: on, access: "full", count: 0, days: 0 };
                })
            );
            payload.features = paidFeatures;
        }

        setLoading(true)
        request({
            url: isEdit ? `${api.addEdit}/${id}` : api.addEdit,
            method: 'POST',
            data: payload,
            onSuccess: (data) => {
                setLoading(false)
                if (data.status) {
                    ShowToast(data.message, Severty.SUCCESS)
                    navigate(`/${routeName}`);
                } else {
                    ShowToast(data.message, Severty.ERROR)
                }
            },
            onError: (error) => {
                ShowToast("Something went wrong, Please try again later", Severty.ERROR)
                setLoading(false)
            },
        })
    };

    useEffect(() => {
        if (isEdit) fetchData(id);
    }, [id]);

    return (
        <Card title={`${isEdit ? "Edit" : "Add New"} ${sectionName}`}>

            <Spin spinning={fetching} tip="Loading subscription details...">

                <Form className="edit-page-wrap" form={form} onFinish={onCreate} autoComplete="off" layout="verticle" name="blog_form">

                    <Row gutter={[24, 0]}>

                        <Col span={24} xs={24} sm={24} md={24}>

                            <Tabs defaultActiveKey={languages[0].short}>

                                {languages.map(lang => (

                                    <TabPane tab={<span>{lang.long} {validationStatus[lang.short] === false && <Badge dot color="red" />}</span>} key={lang.short}>

                                        <Form.Item label={`Name (${lang.long})`} name={`${lang.short}_name`}
                                            rules={[
                                            ...(lang.short === 'en' ? [
                                                { required: true, message: `Please enter the name in ${lang.long}!` }
                                            ] : []),
                                            { max: 100, message: "Name should not contain more than 100 characters!" },
                                            { min: 2, message: "Name should contain at least 2 characters!" },
                                            ]}
                                            initialValue={form.getFieldValue(`${lang.short}_name`)}
                                            onChange={(data) => handleFieldChange(data, lang.short, 'title')}
                                        >

                                            <Input autoComplete="off" placeholder={`Enter name in ${lang.long}`} />
                                        </Form.Item>

                                        <Form.Item label={`Description (${lang.long})`} name={`${lang.short}_description`}
                                            rules={[
                                                { required: lang.short === 'en', message: `Please enter the description in ${lang.long}!` },
                                            ]}
                                            initialValue={form.getFieldValue(`${lang.short}_description`)}
                                        >
                                            <DescriptionEditor placeholder={`Enter Description in ${lang.long}`} onChange={(data)=> handleFieldChange(data, lang.short, 'description')} />

                                        </Form.Item>
                                    </TabPane>
                                ))}
                            </Tabs>
                        </Col>

                        <Col span={24} xs={24} sm={12} md={12}>
                            <Form.Item
                                name="type"
                                label="Subscription Type"
                                rules={[{ required: true, message: "Please select subscription type!" }]}
                            >
                                <Radio.Group
                                    onChange={(e) => { setType(e.target.value); form.setFieldValue("type", e.target.value); }}
                                    value={type}
                                >
                                    <Radio value="trial">Trial</Radio>
                                    <Radio value="paid">Paid</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>

                        {type === "trial" && (
                            <Col span={24}>
                                <Alert
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 16, padding: 16 }}
                                    message="Trial subscription"
                                    description={<span>Newly registered users automatically get a <b>{trialDays || "—"}-day</b>  trial period <Link className="italic text-blue-600 font-semibold" to="/setting" target="_blank" rel="noopener noreferrer">(managed in Settings)</Link> with the features selected below. When the trial ends, the user is automatically switched to <b>Free</b> unless they purchase a paid subscription before the trial period ends.</span>}
                                />
                            </Col>
                        )}

                        {type === "paid" && (
                            <Col span={24}>
                                <Alert
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 16, padding: 16 }}
                                    message="Paid subscription"
                                    description="A paid subscription applies to the selected segment (required). Set the pricing and the features included below."
                                />
                            </Col>
                        )}

                        {type === "paid" && (
                            <Col span={12} xs={12} sm={12} md={8}>
                                <Form.Item className="segment-elt"
                                    name="segment_id"
                                    label="Select Segment"
                                    rules={[{ required: true, message: 'Missing Segment Selection' }]}
                                >
                                    <SegmentSelect
                                        value={form.getFieldValue("segment_id")}
                                        onChange={(val) => form.setFieldValue("segment_id", val)}
                                    />
                                </Form.Item>
                            </Col>
                        )}

                        {type === "paid" && (
                            <>
                                <Col span={24}>
                                    <Divider orientation="center">Pricing Plans (INR)</Divider>
                                </Col>
                                <Col span={24} xs={24} sm={12} lg={6}>
                                    <Form.Item
                                        label="1 Month Price (₹)"
                                        name={["pricing", "price_1_month"]}
                                        rules={[
                                            { required: true, message: "Please enter 1 month price!" },
                                            { pattern: /^[0-9]*$/, message: "Only numeric values allowed" },
                                        ]}
                                    >
                                        <Input maxLength={10} autoComplete="off" placeholder="e.g. 1000" />
                                    </Form.Item>
                                </Col>
                                <Col span={24} xs={24} sm={12} lg={6}>
                                    <Form.Item
                                        label="3 Month Price (₹)"
                                        name={["pricing", "price_3_month"]}
                                        rules={[
                                            { required: true, message: "Please enter 3 month price!" },
                                            { pattern: /^[0-9]*$/, message: "Only numeric values allowed" },
                                        ]}
                                    >
                                        <Input maxLength={10} autoComplete="off" placeholder="e.g. 2850" />
                                    </Form.Item>
                                </Col>
                                <Col span={24} xs={24} sm={12} lg={6}>
                                    <Form.Item
                                        label="6 Month Price (₹)"
                                        name={["pricing", "price_6_month"]}
                                        rules={[
                                            { required: true, message: "Please enter 6 month price!" },
                                            { pattern: /^[0-9]*$/, message: "Only numeric values allowed" },
                                        ]}
                                    >
                                        <Input maxLength={10} autoComplete="off" placeholder="e.g. 5400" />
                                    </Form.Item>
                                </Col>
                                <Col span={24} xs={24} sm={12} lg={6}>
                                    <Form.Item
                                        label="12 Month Price (₹)"
                                        name={["pricing", "price_12_month"]}
                                        rules={[
                                            { required: true, message: "Please enter 12 month price!" },
                                            { pattern: /^[0-9]*$/, message: "Only numeric values allowed" },
                                        ]}
                                    >
                                        <Input maxLength={10} autoComplete="off" placeholder="e.g. 10200" />
                                    </Form.Item>
                                </Col>
                            </>
                        )}
                    </Row>

                    <Divider orientation="center">Subscription Features</Divider>

                    {type === "paid" && (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="Full access — no usage limits"
                            description="Toggle each feature ON (full access) or OFF (no access). Paid subscriptions have no usage metering — number of times / once / valid days limits can be configured for the trial only."
                        />
                    )}

                    <Row gutter={[24, 12]}>
                        {FEATURE_CONFIG.map((group) => (
                            <Col span={24} key={group.category}>
                                <div style={{ background: '#f5f5f5', padding: '8px 16px', borderRadius: 6, marginBottom: 8 }}>
                                    <strong>{group.category}</strong>
                                </div>
                                <Row gutter={[24, 0]} style={{ paddingLeft: 16 }}>
                                    {type === "paid"
                                        ? group.features.map((feature) => (
                                            <PaidFeatureItem key={feature.key} form={form} feature={feature} />
                                        ))
                                        : group.features.map((feature) => (
                                            <FeatureItem key={feature.key} form={form} feature={feature} />
                                        ))}
                                </Row>
                            </Col>
                        ))}
                    </Row>

                    <Form.Item className="btn-row float-right mt-3">
                        <Link className="ant-btn ant-btn-secondary" to={`/${routeName}${location.search}`}>

                        <ArrowLeftIcon size={15} /> Back</Link>

                        <Button type="primary" loading={loading} htmlType="submit">
                            <CheckLineIcon size={15} /> {isEdit ? "Update" : "Submit"}
                        </Button>
                    </Form.Item>
                </Form>

            </Spin>
        </Card>
    )
}

export default AddEdit;
